import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FaStar, FaGoogle, FaCheckCircle, FaHeart, FaTimes } from "react-icons/fa";
import { useTheme } from "../../context/SocketContext";
import { toast } from "../../components/ui/Toast";

const FeedbackModal = ({ onClose, onSubmit }) => {
  const { currentThemeColors } = useTheme();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [hover, setHover] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showGoogleFeedback, setShowGoogleFeedback] = useState(false);

  const handleSubmit = () => {
    if (rating === 0) {
      toast.warning("Please provide a rating");
      return;
    }
    onSubmit(rating, comment);
    setSubmitted(true);

    if (rating >= 4) {
      setShowGoogleFeedback(true);
    } else {
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md animate-scale-in relative overflow-hidden">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all duration-200 z-10"
        >
          <FaTimes className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-dark-700 text-center">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg"
            style={{ 
              background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
              boxShadow: `0 10px 15px -3px var(--glow-color)`
            }}
          >
            <FaHeart className="w-8 h-8 text-white" />
          </div>
          <h2 className="section-title">Share Your Feedback</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">We'd love to hear about your experience</p>
        </div>

        {/* Body */}
        <div className="p-6">
          {!submitted ? (
            <div className="space-y-6">
              {/* Star Rating */}
              <div className="text-center">
                <label className="block text-sm font-semibold text-dark-700 dark:text-gray-300 mb-3">
                  Rate Your Experience
                </label>
                <div className="flex justify-center gap-2">
                  {[...Array(5)].map((_, i) => {
                    const ratingValue = i + 1;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setRating(ratingValue)}
                        onMouseEnter={() => setHover(ratingValue)}
                        onMouseLeave={() => setHover(null)}
                        className="p-1 transition-transform duration-200 hover:scale-110"
                      >
                        <FaStar
                           className={`w-10 h-10 transition-colors duration-200 ${
                            ratingValue <= (hover || rating) 
                              ? 'text-yellow-400 drop-shadow-lg' 
                              : 'text-gray-300 dark:text-dark-600'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-semibold text-dark-700 dark:text-gray-300 mb-2">
                  Additional Comments
                </label>
                <textarea
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us about your experience..."
                  className="w-full p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                className="w-full py-3.5 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl active:scale-[0.98] transition-all duration-200"
                style={{ 
                  background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
                  boxShadow: `0 10px 15px -3px var(--glow-color)`
                }}
              >
                Submit Feedback
              </button>
            </div>
          ) : showGoogleFeedback ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <FaCheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-dark-800 dark:text-white">Thank you for your feedback!</h3>
              <p className="text-gray-600 dark:text-gray-400">Would you mind sharing your experience on Google?</p>
              <a 
                href="https://search.google.com/local/writereview?placeid=ChIJc03yYGCFXjkR5jCfhWzx_uQ" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 shadow-lg shadow-red-500/30 hover:shadow-xl transition-all duration-200"
              >
                <FaGoogle className="w-5 h-5" />
                Leave a Google Review
              </a>
              <button 
                onClick={onClose}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              > 
                Maybe Later
              </button>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <FaCheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-dark-800 dark:text-white">Thank you for your feedback!</h3>
              <p className="text-gray-600 dark:text-gray-400">We appreciate you helping us improve our service.</p>
              <button 
                onClick={onClose}
                className="w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-xl transition-all duration-200"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackModal;
